import React, { Fragment, useEffect, useRef, useState } from 'react'
import styled from 'styled-components'
import ReactToPrint from 'react-to-print'
import dayjs from 'dayjs'
import 'dayjs/locale/nl'
import { Link } from 'react-router-dom'

import { getLocalStorage } from '../../utils'
import { Slide as SlideType, Question as QuestionsType, AnswerStorageType } from '../../types'
import breakpoints from '../../constants/breakpoints'
import ThuisartsLogo from '../../assets/thuisarts-logo.svg'
import i18n from '../../i18n'
import logo from '../../assets/logo.svg'
import Print from '../../assets/print.svg'
import theme from '../../theme'

import Header from './Header'
import Footer from './Footer'
import LinkButton from '../../components/LinkButton'
import Icon from '../../components/Icon'

interface Answer {
  placeholder?: string
  response: string | JSX.Element[]
  feedback?: string
  feedbackColor?: string
  explanation?: string
}

interface Props {
  appURL: string
  routes: SlideType[]
  startLink: string
  backLink: string
  title: string
}

interface RefObject {
  readonly current: HTMLDivElement | null
}

const PrintOverview: React.FC<Props> = ({ appURL, backLink, startLink, title, routes }) => {
  const modalRef: RefObject = useRef<HTMLDivElement>(null)
  const [overviewRef, setOverviewRef] = useState<HTMLDivElement | null>()

  useEffect(() => {
    if (modalRef) {
      modalRef && setOverviewRef(modalRef.current)
    }
  }, [modalRef])

  const getCurrentRoute = () => {
    return routes.filter((item) => {
      return item.type === 'end'
    })[0]
  }

  const state = {
    currentDate: dayjs(new Date(Date.now())).format('DD-MM-YYYY'),
    currentRoute: getCurrentRoute(),
  }

  const getLocalUpdatedAt = (name: string) => {
    let initValue = ''
    if (localStorage.getItem(name)) {
      initValue = localStorage.getItem(name) || ''
      const updatedAt = initValue === null ? new Date().toDateString() : initValue

      return dayjs(updatedAt).locale('nl').format('dddd DD MMMM YYYY, HH:mm uur')
    }

    return new Date().toDateString()
  }

  const getAnswer = (route: SlideType, foundAnswer: AnswerStorageType): Answer[] => {
    const answers: Answer[] = []
    let answer = foundAnswer ? foundAnswer.answer[0] || 'Niet ingevuld' : ''

    if (route.questions) {
      if (route.questions[0].type === 'multiple' || route.questions[0].type === 'single') {
        let answersList = route.questions[0].answers || []
        answersList = answersList.filter((answer) => foundAnswer.answer.includes(answer.title))
        let response = []

        response = answersList.map((answer) => {
          return answersList.length > 1 ? (
            <li key={answer.title}>{answer.title}</li>
          ) : (
            <span key={answer.title}>{answer.title}</span>
          )
        })

        // If questions where skipped the answersList has no length, in that case the answer should be set to "Niet ingevuld".
        if (answersList.length === 0) {
          response.push(<span>Niet ingevuld</span>)
        }

        answers.push({
          response,
          explanation: answersList.length ? foundAnswer.explanation : '',
        })
      } else {
        route.questions.forEach((element: QuestionsType, index: number) => {
          answer = foundAnswer ? foundAnswer.answer[index] || 'Niet ingevuld' : ''

          answers.push({
            placeholder: element.title || '',
            response: answer,
          })
        })
      }
    } else {
      if (route.type === 'uniqueQuestion') {
        let answersList = route.answers || []
        answersList = answersList.filter(({ title }: { title: string }) => title === answer)

        const response = answersList.map(({ title }: { title: string }) => {
          return answersList.length > 1 ? (
            <li key={title}>{title}</li>
          ) : (
            <span key={title}>{title}</span>
          )
        })

        answers.push({ response })
      } else if (route.type === 'feedbackQuestion') {
        answer = foundAnswer ? foundAnswer.answer[0] || 'Niet ingevuld' : ''

        answers.push({
          response: answer,
          feedback: foundAnswer.feedback,
          feedbackColor: foundAnswer.feedbackColor,
        })
      } else {
        answers.push({ response: answer || '' })
      }
    }

    return answers
  }

  const renderOverview = () => {
    const answers = getLocalStorage('answers')
    const chapters = routes.filter((route) =>
      answers.some((answer) => answer.chapter === route.routeId),
    )

    return chapters.map((chapter, index) => {
      const chapterAnswers = answers.filter((answer) => answer.chapter === chapter.routeId)
      const questions = chapterAnswers.map((answer) => {
        const foundQuestionSlide = routes.find(
          (route) => route.routeId === answer.routeId && !route.hideFromOverview,
        )

        if (foundQuestionSlide && foundQuestionSlide.type === 'question') {
          if (foundQuestionSlide.questions && foundQuestionSlide.questions[0].type === 'multiple') {
            return {
              question: foundQuestionSlide.title,
              type: foundQuestionSlide.type,
              hideFromOverview: foundQuestionSlide.hideFromOverview,
              answers: foundQuestionSlide
                ? getAnswer(foundQuestionSlide, answer)
                : [{ response: '' }],
            }
          }
        }

        return {
          question: foundQuestionSlide ? foundQuestionSlide.title : '',
          type: foundQuestionSlide ? foundQuestionSlide.type : '',
          hideFromOverview: foundQuestionSlide ? foundQuestionSlide.hideFromOverview : true,
          answers: foundQuestionSlide ? getAnswer(foundQuestionSlide, answer) : [{ response: '' }],
        }
      })

      const hideFromOverview = questions.every(({ hideFromOverview }) => hideFromOverview)

      return (
        <ResultBlock key={index} $hide={hideFromOverview}>
          {questions &&
            questions
              .filter(({ hideFromOverview }) => !hideFromOverview)
              .map(({ question, answers }, key) => (
                <React.Fragment key={key}>
                  <h2>{question}</h2>
                  <Answers>
                    {answers.map((answer, k) => (
                      <div key={k}>
                        {answer.placeholder && (
                          <Fragment>
                            <label dangerouslySetInnerHTML={{ __html: answer.placeholder }} />:
                          </Fragment>
                        )}

                        {Array.isArray(answer.response) && answer.response.length > 1 ? (
                          <ul>{answer.response}</ul>
                        ) : (
                          <p>{answer.response}</p>
                        )}

                        {answer.explanation && answer.explanation.length && (
                          <Explanation>
                            <h4>Toelichting</h4>
                            <p>{answer.explanation} </p>
                          </Explanation>
                        )}

                        {answer.feedback && (
                          <Response
                            $feedbackColor={
                              answer.feedbackColor ? answer.feedbackColor : theme.yellow
                            }
                          >
                            <p dangerouslySetInnerHTML={{ __html: answer.feedback }} />
                          </Response>
                        )}
                      </div>
                    ))}
                  </Answers>
                </React.Fragment>
              ))}
        </ResultBlock>
      )
    })
  }

  const { currentDate, currentRoute } = state
  const completedAt = getLocalUpdatedAt('updatedAt')
  const advices = routes.filter((route) => route.showAdviceInOverview)

  return (
    <OverviewContainer id="readspeakerArea">
      <div>
        <Header overviewRef={overviewRef} text={currentRoute.header ? currentRoute.header : null} />
        <PrintOverviewContainer ref={modalRef}>
          <Content className="text-formatted">
            <PrintHeader className="rs_skip">
              <CurrentDate>{currentDate}</CurrentDate>
              <Slogan>{title}</Slogan>
              <Letterhead>
                <AppInfo>
                  {title}
                  <Link to={appURL}>{appURL}</Link>
                </AppInfo>
                <img src={ThuisartsLogo} width="130" height="29" alt="Thuisarts logo" />
                <SubmittedDate>
                  {i18n.t('print.submitedAt')} <strong>{completedAt} </strong>
                </SubmittedDate>
              </Letterhead>
            </PrintHeader>

            <OverviewHeader>
              <img src={logo} width="35" height="35" alt="Thuisarts logo" />
              {title}
            </OverviewHeader>

            <PrintBlock>
              <h2>{i18n.t('print.yourAnswers')}</h2>
              <ReactToPrint
                trigger={() => (
                  <LinkButton tertiary>
                    <Fragment>
                      <Icon icon={Print} />
                      {i18n.t('print.print')}
                    </Fragment>
                  </LinkButton>
                )}
                content={() => modalRef.current || document.createElement('<span>')}
              />
            </PrintBlock>

            <Overview>{renderOverview()}</Overview>

            {advices && advices.length > 0 && (
              <>
                <OverviewTitle>Voedingsadviezen</OverviewTitle>
                <>
                  <Overview>
                    {advices.map((advice) => {
                      if (advice.description && advice.description.length > 0)
                        return (
                          <Advice
                            key={`advice-${advice.routeId}`}
                            dangerouslySetInnerHTML={{ __html: advice.description }}
                          />
                        )
                    })}
                  </Overview>
                </>
              </>
            )}

            {currentRoute.helpText && (
              <HelpBlock dangerouslySetInnerHTML={{ __html: currentRoute.helpText }} />
            )}

            <FooterInfo>
              {i18n.t('print.submitedAt')} {completedAt}
            </FooterInfo>
          </Content>

          <FooterButtons>
            <LinkButton to={startLink}>{i18n.t('print.reEnter')}</LinkButton>
            <LinkButton to={backLink} tertiary>
              Terug naar Thuisarts
            </LinkButton>
          </FooterButtons>
        </PrintOverviewContainer>
        <Footer />
      </div>
    </OverviewContainer>
  )
}

const OverviewContainer = styled.div`
  position: fixed;
  overflow: auto;
  height: calc(100% - var(--headerHeight));
  min-height: calc((var(--vh) * 100) - var(--headerHeight));
  width: 100%;
  top: var(--headerHeight);
  left: 0;
  background-color: white;
  display: flex;
  flex-direction: column;
`

const PrintOverviewContainer = styled.div`
  background-color: ${({ theme }) => theme.lighterGrey};
  padding-top: 100px;
  padding-bottom: 65px;
  height: auto;
  min-height: auto;

  @media print {
    background-color: white;
    padding: 0 25px;
  }
`

const Content = styled.div`
  background-color: white;
  margin: 0 20px;
  position: relative;

  @media (min-width: ${breakpoints.md}) {
    margin: 0 auto;
    max-width: 694px;
  }

  @media print {
    width: 100%;
    padding-bottom: 30px;
    margin: 0;
  }
`

const PrintHeader = styled.div`
  color: ${({ theme }) => theme.textColor};
  display: none;
  font:
    0.5rem 'Times New Roman',
    Times,
    serif;
  padding: 20px;
  position: relative;

  @media print {
    display: block;
  }
`

const CurrentDate = styled.span`
  position: absolute;
  left: 20px;
  top: 20px;
`

const Slogan = styled.div`
  text-align: center;
`

const Letterhead = styled.div`
  align-items: center;
  border-bottom: 1px solid ${({ theme }) => theme.lightGrey};
  display: flex;
  font-family: 'Circular', sans-serif;
  justify-content: space-between;
  padding: 25px 0;

  & a {
    font-size: 0.5rem;
  }
`

const SubmittedDate = styled.div`
  display: flex;
  flex-basis: 0;
  flex-direction: column;
  flex-grow: 1;
  text-align: right;
`

const AppInfo = styled.div`
  display: flex;
  flex-basis: 0;
  flex-direction: column;
  flex-grow: 1;
  font-size: 0.5625rem;
  font-weight: 900;

  & a {
    font-weight: normal;
    text-decoration: none;
  }
`

const OverviewHeader = styled.header`
  border-bottom: 1px solid ${({ theme }) => theme.lightGrey};
  color: ${({ theme }) => theme.textColor};
  font-size: 1rem;
  font-weight: bold;
  line-height: 2;
  padding: 20px 20px 25px 20px;
  text-align: center;

  & img {
    display: block;
    margin: 0 auto;
  }

  @media (min-width: ${breakpoints.md}) {
    padding: 20px 40px 25px 40px;

    & img {
      display: inline;
      margin-right: 10px;
      vertical-align: middle;
    }
  }

  @media print {
    display: none;
  }
`

const PrintBlock = styled.div`
  display: flex;
  justify-content: space-between;
  padding: 25px 20px;
  align-items: center;
  border-bottom: 1px solid ${({ theme }) => theme.lightGrey};

  & h2 {
    font-size: 1.25rem;
    font-weight: bold;
    color: ${({ theme }) => theme.textColor};
    margin: 0;
  }

  & button {
    width: auto;
    padding: 12px;
    font-size: 1rem;

    & svg {
      fill: ${({ theme }) => theme.textColor};
      width: 15px;
      margin-right: 10px;
      vertical-align: middle;
    }

    @media print {
      display: none;
    }
  }

  @media (min-width: ${breakpoints.sm}) {
    padding: 35px 40px;
  }
`

const Overview = styled.div`
  margin: 0;
  padding: 25px 20px;
  border-bottom: 1px solid ${({ theme }) => theme.lightGrey};

  @media (min-width: ${breakpoints.md}) {
    padding: 35px 40px;
  }
`

const OverviewTitle = styled.h2`
  display: flex;
  justify-content: space-between;
  padding: 25px 20px;
  align-items: center;
  border-bottom: 1px solid ${({ theme }) => theme.lightGrey};
  font-size: 1.25rem;
  font-weight: bold;
  color: ${({ theme }) => theme.textColor};
  margin: 0;

  @media (min-width: ${breakpoints.sm}) {
    padding: 35px 40px;
  }
`

const ResultBlock = styled.div<{ $hide: boolean }>`
  ${({ $hide }) =>
    $hide &&
    `
    display: none;
  `}

  & h2 {
    display: inline;
    margin: 0;
    font-size: 1.5rem;
    font-weight: 900;
  }

  & p {
    font-weight: normal;
    font-size: 1.125rem;
    margin: 0 0 30px 0;
    line-height: 1.61;
  }

  & ul {
    margin: 0 0 30px 0;

    li {
      font-weight: normal;
      font-size: 1.125rem;
    }
  }

  @media print {
    border: none;
    padding: 12px 20px;
    font-size: 0.8125rem;

    & h3 {
      font-size: 0.835rem;
    }

    & p {
      font-size: 0.625rem;
      line-height: 1.8;
      margin: 0 0 10px 0;
    }

    & ul {
      margin: 0 0 20px 0;

      li {
        margin-top: 0;
        font-size: 0.625rem;
      }
    }
  }
`

const Answers = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  width: 100%;
  font-size: 1rem;
  margin-top: 8px;

  & div {
    width: 100%;
    padding-right: 10px;

    &:last-child {
      padding-right: 0;
    }

    & label {
      line-height: 1.8;
      font-weight: bold;
    }
  }

  @media (min-width: ${breakpoints.sm}) {
    flex-direction: row;
  }

  @media print {
    flex-direction: row;
    font-size: 0.625rem;
  }
`

const Explanation = styled.div`
  margin-top: -5px;
`

const Response = styled.div<{ $feedbackColor: string }>`
  padding: 32px !important;
  background-color: ${theme.lightestGrey};
  border-left: 3px solid ${({ $feedbackColor }) => $feedbackColor};
  margin-bottom: 40px;

  & ul:last-child {
    margin-bottom: 0;
  }

  & p:last-child {
    margin-bottom: 0;
  }
`

const Advice = styled.div`
  padding: 32px !important;
  background-color: ${theme.lightestGrey};
  border-left: 3px solid ${theme.yellow};
  margin-bottom: 40px;

  & ul:last-child {
    margin-bottom: 0;
  }

  & p:last-child {
    margin-bottom: 0;
  }
`

const HelpBlock = styled.div`
  font-size: 1rem;
  padding: 20px 20px 27px 20px;
  border-bottom: 1px solid ${({ theme }) => theme.lightGrey};
`

const FooterInfo = styled.div`
  font-size: 1rem;
  font-weight: 600;
  line-height: 2;
  padding: 20px 20px 27px 20px;
  text-align: center;

  @media print {
    display: none;
  }
`

const FooterButtons = styled.div`
  display: flex;
  flex-direction: column;
  margin: 65px 20px 0;
  text-align: center;
  position: relative;

  & a {
    display: block;
    margin: 8px 0;
  }

  @media (min-width: ${breakpoints.md}) {
    flex-direction: row;
    margin: 65px auto 0;
    max-width: 694px;

    & a {
      display: block;
      margin: 8px;

      &:first-child {
        margin-left: 0;
      }

      &:last-child {
        margin-right: 0;
      }
    }
  }

  @media print {
    width: 100%;
    padding-bottom: 30px;
    margin: 0;
  }

  @media print {
    display: none;
  }
`

export default PrintOverview
